import { useMemo } from 'react'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { Tooltip } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'

import { IGridColumn } from 'components/common'
import { BookingStatus } from 'generated/graphql'

const useGetAllocatedDriversColumns = (
  isAllowedToViewPrimaryDriversList,
  isAllowedToViewCurrentSupplierSubDriversList,
  handleDriverNotes,
  deleteBookingDriverProcessing,
  handleEditAllocatedDriver,
  handleAddRating,
  handleDeleteDriver,
  bookingStatus,
) => {
  const bookingStatusForRating =
    bookingStatus &&
    [BookingStatus.Invoice, BookingStatus.Complete, BookingStatus.ProofOfDelivery].includes(
      bookingStatus,
    )

  const bookingStatusForDeleteAllocation =
    bookingStatus &&
    [
      BookingStatus.ProofOfDelivery,
      BookingStatus.Complete,
      BookingStatus.Invoice,
      BookingStatus.ScheduledTemplate,
    ].includes(bookingStatus)

  const bookingStatusForReplaceAction =
    bookingStatus &&
    [
      BookingStatus.Quotation,
      BookingStatus.EBooking,
      BookingStatus.Waiting,
      BookingStatus.Allocated,
      BookingStatus.Declined,
      BookingStatus.ScheduledTemplate,
      BookingStatus.PackageOnBoardOnWay,
      BookingStatus.PackageOnBoardArrival,
    ].includes(bookingStatus)

  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'primaryDriverName',
        headerName: 'Company',
        minWidth: 150,
        flex: 1,
        sortable: false,
        valueGetter: ({ row }) => row?.driver.primaryDriverName,
      },
      {
        field: 'driver',
        headerName: 'Driver',
        minWidth: 150,
        flex: 1,
        sortPath: ['driver', 'name'],
        valueGetter: ({ value }) => value?.name,
      },
      { field: 'allocationStatus', headerName: 'Alloc. Status', width: 100 },
      {
        field: 'isUsingInternalApp',
        headerName: 'Using App?',
        width: 100,
        type: 'boolean',
        sortable: false,
        valueGetter: ({ row }) => row.driver?.isUsingInternalApp,
      },
      {
        field: 'reference',
        headerName: 'Reference',
        width: 100,
        type: 'string',
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        width: 120,
        sortPath: ['vehicle', 'vehicleType', 'name'],
        valueGetter: ({ value }) => value?.vehicleType?.name,
      },
      { field: 'charge', headerName: 'Charge', width: 80, type: 'number' },
      { field: 'extraPayment', headerName: 'Extra Pay', width: 80, type: 'number' },
      {
        field: 'currency',
        headerName: 'Ccy',
        width: 50,
        sortPath: ['currency', 'code'],
        valueGetter: ({ value }) => value?.code,
      },
      ...(isAllowedToViewPrimaryDriversList
        ? [
            {
              field: 'xeroPurchaseCode',
              headerName: 'Purchase Code',
              width: 120,
              type: 'number',
            },
          ]
        : []),
      { field: 'createdAt', headerName: 'Created At', hide: true },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        type: 'actions',
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={`note-${id}`}
            label='Driver note'
            size='medium'
            disabled={!row.driverNotes}
            icon={
              <Tooltip title={'Driver note'} arrow>
                <StickyNote2OutlinedIcon />
              </Tooltip>
            }
            onClick={handleDriverNotes(row.driverNotes)}
          />,
          <GridActionsCellItem
            key={`edit-${id}`}
            label='Edit'
            size='medium'
            disabled={
              deleteBookingDriverProcessing ||
              (!bookingStatusForReplaceAction && isAllowedToViewCurrentSupplierSubDriversList)
            }
            icon={
              <Tooltip title={'Edit'} arrow>
                <EditIcon />
              </Tooltip>
            }
            onClick={handleEditAllocatedDriver(row)}
          />,
          <GridActionsCellItem
            sx={{ display: bookingStatusForRating ? 'block' : 'none' }}
            key={`rating-${id}`}
            label='Rating'
            size='medium'
            icon={
              <Tooltip title={'Rating'} arrow>
                {row.rating ? <StarIcon sx={{ color: '#faaf00' }} /> : <StarBorderIcon />}
              </Tooltip>
            }
            onClick={handleAddRating(row.driver.id)}
          />,
          <GridActionsCellItem
            key={`delete-${id}`}
            label='Delete'
            size='medium'
            disabled={deleteBookingDriverProcessing || bookingStatusForDeleteAllocation}
            icon={
              <Tooltip title={'Delete'} arrow>
                <DeleteIcon />
              </Tooltip>
            }
            onClick={handleDeleteDriver(id)}
          />,
        ],
      },
    ],
    [
      isAllowedToViewPrimaryDriversList,
      handleDriverNotes,
      deleteBookingDriverProcessing,
      bookingStatusForReplaceAction,
      isAllowedToViewCurrentSupplierSubDriversList,
      handleEditAllocatedDriver,
      bookingStatusForRating,
      handleAddRating,
      bookingStatusForDeleteAllocation,
      handleDeleteDriver,
    ],
  )

  return columns
}
export default useGetAllocatedDriversColumns
